import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

export const getServices = createAsyncThunk("services/getServices", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/company/ircec");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const getService = createAsyncThunk(
    "services/getService",
    async ({ serviceId }: { serviceId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/company/ircec/${serviceId}`);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const createService = createAsyncThunk(
    "services/createService",
    async ({ data }: { data: Omit<IService, "id" | "companyId"> }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/company/ircec`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const updateService = createAsyncThunk(
    "services/updateService",
    async (
        { serviceId, data }: { serviceId: number; data: Omit<IService, "id" | "companyId"> },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/company/ircec/${serviceId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteService = createAsyncThunk(
    "services/deleteService",
    async ({ serviceId }: { serviceId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/company/ircec/${serviceId}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface IService {
    id: number;
    companyId: number;
    name: string;
    racd: number;
    raap: number;
    sacd: number;
    hasIrcec: number;
}

interface InitialState {
    services: IService[];
    service: IService;
    servicesStatus: TFetchStatus;
}

const initialState: InitialState = {
    services: [],
    service: {} as IService,
    servicesStatus: "idle",
};

const servicesStore = createSlice({
    name: "services",
    initialState,
    reducers: {},
    extraReducers(builders) {
        builders.addCase(getServices.fulfilled, (state, { payload }) => {
            state.services = payload;
            state.servicesStatus = "succeeded";
        });
        builders.addCase(getServices.pending, (state) => {
            state.servicesStatus = "pending";
        });
        builders.addCase(getServices.rejected, (state) => {
            state.servicesStatus = "failed";
        });
        builders.addCase(getService.fulfilled, (state, { payload }) => {
            state.service = payload;
            state.servicesStatus = "succeeded";
        });
        builders.addCase(getService.rejected, (state) => {
            state.servicesStatus = "failed";
        });
        builders.addCase(createService.fulfilled, (state, { payload }) => {
            state.services = [...state.services, payload];
            toast.success(`${t("Services.Store.CreateSuccess")}`);
        });
        builders.addCase(createService.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builders.addCase(updateService.fulfilled, (state, { payload }) => {
            state.services = [...state.services].map((item) => {
                if (item.id === payload.id) {
                    return payload;
                } else {
                    return item;
                }
            });
            toast.success(`${t("Services.Store.EditSuccess")}`);
        });
        builders.addCase(updateService.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builders.addCase(deleteService.fulfilled, (state, action) => {
            state.services = [...state.services].filter((item) => item.id !== action.meta.arg.serviceId);
            toast.success(`${t("Services.Store.DeleteSuccess")}`);
        });
        builders.addCase(deleteService.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default servicesStore.reducer;
